import React, { useEffect, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { useNavigate } from "react-router-dom";
import NavbarMob from "./NavbarMob";
import Navbar from "./Navbar";
import Footer from "./Footer";
import Navbar2 from "./Navbar2";
import Navbar2Mob from "./Navbar2Mob";

const DeleteProfileCan = () => {
    const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
    const [candidates, setCandidates] = useState([]);
    const [candidateSearch, setCandidateSearch] = useState("");
    const [blockedProfiles, setBlockedProfiles] = useState([]);
    const [loading, setLoading] = useState(true);

    // Modal & Security States
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deletePassword, setDeletePassword] = useState("");
    const [candidateToDelete, setCandidateToDelete] = useState(null);
    const [passwordError, setPasswordError] = useState("");

    const navigate = useNavigate();
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
    const customerType = sessionStorage.getItem("customerType");
    const companyName = sessionStorage.getItem("customerName");
    const hasRiyaHubAccess = (companyName || "").trim().toUpperCase() === "RIYA HUB";
    const isAuthorized = customerType === "admin" || customerType === "mainAdmin" || hasRiyaHubAccess;
    const passwordAccess = customerType === "mainAdmin" || hasRiyaHubAccess;

    // Secure Admin Password (from .env)
    const DELETE_ADMIN_PASSWORD = process.env.REACT_APP_DELETE_ADMIN_PASSWORD;

    useEffect(() => {
        if (!isAuthorized) {
            navigate("/login", { replace: true });
        }
    }, [isAuthorized, navigate]);

    // Fetch Candidates
    useEffect(() => {
        if (!isAuthorized) {
            setLoading(false);
            return;
        }

        const fetchCandidates = async () => {
            try {
                const response = await fetch(`${apiBaseUrl}/getAllCandidates`);
                const data = await response.json();
                // console.log(data);

                // Sort by created_at (newest first)
                const sortedData = (data || []).sort((a, b) => {
                    const dateA = new Date(a.created_at || a.CreatedAt || a.createdAt || 0);
                    const dateB = new Date(b.created_at || b.CreatedAt || b.createdAt || 0);
                    return dateB - dateA; // Descending: newest first
                });

                setCandidates(sortedData);
                // console.log("Sorted candidates (newest first):", sortedData);
            } catch (error) {
                console.error("Error fetching candidates:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchCandidates();
    }, [apiBaseUrl, isAuthorized]);

    // Posted date
    const getJoinedTimeDisplay = (createdAt) => {
        if (!createdAt) return "Date not available";

        return new Date(createdAt).toLocaleDateString("en-GB");
        // → 09/01/2026    (clean, unambiguous, widely understood)
    };

    // Fetch Blocked Profiles
    useEffect(() => {
        if (!isAuthorized) return;

        const fetchBlockedProfiles = async () => {
            try {
                const response = await fetch(`${apiBaseUrl}/getBlockedProfiles`);
                const data = await response.json();
                const normalized = (data || []).map((profile) => ({
                    blockedId: profile.BlockedId,
                    profileType: profile.ProfileType,
                    mobileNumber: profile.MobileNumber,
                }));
                setBlockedProfiles(normalized);
            } catch (error) {
                console.error("Error fetching blocked profiles:", error);
            }
        };
        fetchBlockedProfiles();
    }, [apiBaseUrl, isAuthorized]);

    // Open Delete Modal
    const openDeleteModal = (candidateID) => {
        setCandidateToDelete(candidateID);
        setDeletePassword("");
        setPasswordError("");
        setShowDeleteModal(true);
    };

    // Confirm Delete with Password
    const confirmDeleteWithPassword = async () => {
        if (deletePassword !== DELETE_ADMIN_PASSWORD) {
            setPasswordError("Incorrect password. Deletion denied.");
            return;
        }

        if (!candidateToDelete) return;

        try {
            const response = await fetch(`${apiBaseUrl}/deleteCandidate/${candidateToDelete}`, {
                method: "DELETE",
            });
            const data = await response.json();

            if (data.success) {
                setCandidates((prev) => prev.filter((c) => c.CandidateID !== candidateToDelete));
                closeDeleteModal();
                alert("Candidate deleted successfully.");
            } else {
                setPasswordError("Failed to delete: " + (data.message || "Unknown error"));
            }
        } catch (error) {
            console.error("Error deleting candidate:", error);
            setPasswordError("Network error. Please try again.");
        }
    };

    // Close Modal
    const closeDeleteModal = () => {
        setShowDeleteModal(false);
        setCandidateToDelete(null);
        setDeletePassword("");
        setPasswordError("");
    };

    // Block Profile
    const handleBlockProfile = async (blockedId, mobileNumber, profileType) => {
        const confirmBlock = window.confirm("Are you sure you want to block this profile?");
        if (!confirmBlock) return;

        try {
            const response = await fetch(`${apiBaseUrl}/blockProfile`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ blockedId, profileType, mobileNumber }),
            });
            const data = await response.json();

            if (data.success) {
                setBlockedProfiles((prev) => [...prev, { blockedId, profileType, mobileNumber }]);
                alert("Profile blocked successfully.");
            }
        } catch (error) {
            console.error("Error blocking profile:", error);
        }
    };

    // Unblock Profile
    const handleUnblockProfile = async (blockedId, mobileNumber) => {
        const confirmUnblock = window.confirm("Are you sure you want to unblock this profile?");
        if (!confirmUnblock) return;

        try {
            const response = await fetch(`${apiBaseUrl}/unblockProfile`, {
                method: "DELETE",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ blockedId, mobileNumber }),
            });
            const data = await response.json();

            if (data.success) {
                setBlockedProfiles((prev) =>
                    prev.filter((p) => !(p.blockedId === blockedId && p.mobileNumber === mobileNumber)),
                );
                alert("Profile unblocked successfully.");
            }
        } catch (error) {
            console.error("Error unblocking profile:", error);
        }
    };

    // Check if profile is blocked
    const isBlocked = (id, mobile) => blockedProfiles.some((p) => p.blockedId === id && p.mobileNumber === mobile);

    // Filter Candidates
    const filteredCandidates = candidates.filter((candidate) => {
        const rawSearch = candidateSearch.trim().toLowerCase();
        if (!rawSearch) return true;

        const terms = rawSearch
            .split(",")
            .map((t) => t.trim())
            .filter((t) => t.length > 0);

        if (terms.length === 0) return true;

        const data = {
            name: (candidate.Name || "").toString().toLowerCase(),
            id: (candidate.CandidateID || "").toString(),
            gender: (candidate.Gender || "").toString().toLowerCase(),
            district: (candidate.District || "").toString().toLowerCase(),
            bigTown: (candidate.bigTown || "").toString().toLowerCase(),
            exactLocation: (candidate.exactLocation || "").toString().toLowerCase(),
            email: (candidate.Email || "").toString().toLowerCase(),
            age: (candidate.Age || "").toString(),
            degree: (candidate.Degree || "").toString().toLowerCase(),
            experienced: (candidate.experienced || "").toString().toLowerCase(),
            jobs: (candidate.Jobs || "").toString().toLowerCase(),
            plan: (candidate.SelectedPlan || "").toString().toLowerCase(),
        };

        // Clean the candidate's mobile once (remove non-digits)
        const cleanedMobile = (candidate.Mobile || "").toString().replace(/\D/g, "");

        // Clean each search term for mobile matching (remove +91, spaces, non-digits)
        const cleanedSearchTerms = terms.map((term) =>
            term
                .replace(/^\+91|\+91|\+/g, "")
                .replace(/\s/g, "")
                .replace(/\D/g, ""),
        );

        return terms.every((term, index) => {
            const cleanedTerm = cleanedSearchTerms[index];

            // Special case: gender strict match
            if (["male", "female", "m", "f"].includes(term)) {
                const isMale = ["male", "m"].includes(term);
                const isFemale = ["female", "f"].includes(term);
                return (isMale && data.gender === "male") || (isFemale && data.gender === "female");
            }

            // Mobile match: only if the cleaned search term is non-empty and is contained in the cleaned mobile
            const isMobileMatch = cleanedTerm && cleanedMobile.includes(cleanedTerm);

            // Normal partial match on all other fields
            return (
                isMobileMatch ||
                data.name.includes(term) ||
                data.id.includes(term) ||
                data.district.includes(term) ||
                data.bigTown.includes(term) ||
                data.exactLocation.includes(term) ||
                data.email.includes(term) ||
                data.age.includes(term) ||
                data.degree.includes(term) ||
                data.experienced.includes(term) ||
                data.jobs.includes(term) ||
                data.plan.includes(term)
            );
        });
    });

    if (!isAuthorized) {
        return null;
    }

    return (
        <div className="flex flex-col min-h-screen bg-gray-50">
            {/* Navigation */}
            {isMobile ? <NavbarMob /> : <Navbar />}
            <div className="md:flex hidden">
                <Navbar2 />
            </div>
            <div className="md:hidden flex flex-col">
                <Navbar2Mob />
            </div>

            {/* Main Content */}
            <div className="flex flex-col lg:px-12 px-3 py-10 space-y-8 flex-1">
                <div className="bg-[#d4d4d4] shadow-md rounded-lg p-6">
                    <h2 className="text-3xl font-semibold text-center mb-6 font-display text-gray-800">
                        Manage Candidates
                    </h2>

                    {/* Search Bar */}
                    <input
                        type="text"
                        placeholder="Search by Name, ID, Mobile, Gender, Age, Qualification, Jobs..."
                        value={candidateSearch}
                        onChange={(e) => setCandidateSearch(e.target.value)}
                        className="mb-6 w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                    />

                    {/* Loading State */}
                    {loading ? (
                        <div className="text-center py-10">
                            <p className="text-gray-600">Loading candidates...</p>
                        </div>
                    ) : filteredCandidates.length === 0 ? (
                        <div className="text-center py-10">
                            <p className="text-gray-600">No candidates found.</p>
                        </div>
                    ) : (
                        /* Candidates Grid */
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                            {filteredCandidates.map((candidate) => {
                                const hasPlan =
                                    candidate.SelectedPlan &&
                                    // candidate.SelectedPlan !== "0" &&
                                    candidate.SelectedPlan.trim() !== "";

                                return (
                                    <div
                                        key={candidate.CandidateID}
                                        className={`
                rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow
                ${hasPlan ? "bg-blue-200 text-gray-900" : "bg-white text-gray-900"}
            `}
                                    >
                                        <div className="p-5 text-left space-y-1">
                                            <h3
                                                className={`
                    font-bold text-xl font-display truncate
                    ${hasPlan ? "text-gray-800" : "text-gray-800"}
                `}
                                            >
                                                {candidate.Name}
                                            </h3>

                                            {candidate.SelectedPlan && (
                                                <p
                                                    className={`
                        flex gap-2 font-semibold
                        ${hasPlan ? "text-gray-600" : "text-gray-600"}
                    `}
                                                >
                                                    Selected Plan:
                                                    <span className="font-normal">
                                                        {candidate.SelectedPlan === "0"
                                                            ? "Custom Plan"
                                                            : candidate.SelectedPlan}
                                                    </span>
                                                </p>
                                            )}

                                            <p
                                                className={`
                    flex gap-2 font-semibold
                    ${hasPlan ? "text-gray-600" : "text-gray-600"}
                `}
                                            >
                                                ID:
                                                <span className="font-normal">{candidate.CandidateID}</span>
                                            </p>

                                            <p
                                                className={`
                    flex gap-2 font-semibold
                    ${hasPlan ? "text-gray-600" : "text-gray-600"}
                `}
                                            >
                                                Registration Date:
                                                <span className="font-normal">
                                                    {getJoinedTimeDisplay(candidate.CreatedAt)}
                                                </span>
                                            </p>

                                            <p
                                                className={`
                    flex gap-2 font-semibold
                    ${hasPlan ? "text-gray-600" : "text-gray-600"}
                `}
                                            >
                                                District:
                                                <span className="font-normal">{candidate.District}</span>
                                            </p>

                                            <p
                                                className={`
                    flex gap-2 font-semibold
                    ${hasPlan ? "text-gray-600" : "text-gray-600"}
                `}
                                            >
                                                Town:
                                                <span className="font-normal">{candidate.bigTown}</span>
                                            </p>

                                            <p
                                                className={`
                    flex gap-2 font-semibold
                    ${hasPlan ? "text-gray-600" : "text-gray-600"}
                `}
                                            >
                                                Location:
                                                <span className="font-normal">{candidate.exactLocation}</span>
                                            </p>

                                            <p
                                                className={`
                    flex gap-2 font-semibold
                    ${hasPlan ? "text-gray-600" : "text-gray-600"}
                `}
                                            >
                                                Email:
                                                <span className="font-normal">{candidate.Email}</span>
                                            </p>

                                            <p
                                                className={`
                    flex gap-2 font-semibold
                    ${hasPlan ? "text-gray-600" : "text-gray-600"}
                `}
                                            >
                                                Mobile:
                                                <span className="font-normal">{candidate.Mobile}</span>
                                            </p>

                                            <p
                                                className={`
                    flex gap-2 font-semibold
                    ${hasPlan ? "text-gray-600" : "text-gray-600"}
                `}
                                            >
                                                Whatsapp:
                                                <span className="font-normal">{candidate.WhatsApp}</span>
                                            </p>

                                            {passwordAccess && (
                                                <p
                                                    className={`
                        flex gap-2 font-semibold
                        ${hasPlan ? "text-gray-600" : "text-gray-600"}
                    `}
                                                >
                                                    Password:
                                                    <span className="font-normal">{candidate.Password}</span>
                                                </p>
                                            )}

                                            <p
                                                className={`
                    flex gap-2 font-semibold
                    ${hasPlan ? "text-gray-600" : "text-gray-600"}
                `}
                                            >
                                                Gender:
                                                <span className="font-normal">{candidate.Gender}</span>
                                            </p>

                                            <p
                                                className={`
                    flex gap-2 font-semibold
                    ${hasPlan ? "text-gray-600" : "text-gray-600"}
                `}
                                            >
                                                Age:
                                                <span className="font-normal">{candidate.Age}</span>
                                            </p>

                                            <p
                                                className={`
                    flex gap-2 font-semibold
                    ${hasPlan ? "text-gray-600" : "text-gray-600"}
                `}
                                            >
                                                Qualification:
                                                <span className="font-normal">{candidate.Degree}</span>
                                            </p>

                                            <p
                                                className={`
                    flex gap-2 font-semibold
                    ${hasPlan ? "text-gray-600" : "text-gray-600"}
                `}
                                            >
                                                Experience:
                                                <span className="font-normal">{candidate.experienced}</span>
                                            </p>

                                            <p
                                                className={`
                    font-semibold mt-2 flex-1 overflow-auto
                    ${hasPlan ? "text-gray-600" : "text-gray-600"}
                `}
                                            >
                                                Preferred Jobs:
                                                <span
                                                    className={`
                        ml-2 font-bold
                        ${hasPlan ? "text-black" : "text-gray-900"}
                    `}
                                                >
                                                    {candidate.Jobs}
                                                </span>
                                            </p>

                                            {/* Action Buttons - kept original colors for visibility */}
                                            <div className="mt-4 pt-3 border-t border-gray-200 flex flex-wrap gap-2 justify-around">
                                                {isBlocked(candidate.CandidateID, candidate.Mobile) ? (
                                                    <button
                                                        onClick={() =>
                                                            handleUnblockProfile(candidate.CandidateID, candidate.Mobile)
                                                        }
                                                        className="text-green-600 hover:text-green-800 font-medium text-sm"
                                                    >
                                                        Unblock
                                                    </button>
                                                ) : (
                                                    <button
                                                        onClick={() =>
                                                            handleBlockProfile(
                                                                candidate.CandidateID,
                                                                candidate.Mobile,
                                                                "candidate",
                                                            )
                                                        }
                                                        className="text-red-600 hover:text-red-800 font-medium text-sm"
                                                    >
                                                        Block
                                                    </button>
                                                )}

                                                {customerType === "mainAdmin" && (
                                                    <button
                                                        onClick={() =>
                                                            navigate(`/change-password/${candidate.CandidateID}`)
                                                        }
                                                        className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                                                    >
                                                        Change Password
                                                    </button>
                                                )}
                                                {customerType === "mainAdmin" && (
                                                    <button
                                                        onClick={() => navigate(`/change-mobile/${candidate.CandidateID}`)}
                                                        className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                                                    >
                                                        Change Mobile Number
                                                    </button>
                                                )}

                                                <button
                                                    onClick={() => openDeleteModal(candidate.CandidateID)}
                                                    className="text-red-600 hover:text-red-800 font-medium text-sm"
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl transform transition-all">
                        <h3 className="text-2xl font-bold text-red-600 mb-3">Delete Candidate</h3>
                        <p className="text-gray-700 mb-5">
                            This action <strong>cannot be undone</strong>. The candidate profile will be permanently
                            removed.
                        </p>

                        <label className="block text-sm font-semibold text-gray-700 mb-2">Admin Password Required</label>
                        <input
                            type="password"
                            value={deletePassword}
                            onChange={(e) => {
                                setDeletePassword(e.target.value);
                                setPasswordError("");
                            }}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 mb-3"
                            placeholder="Enter admin password"
                            autoFocus
                        />

                        {passwordError && <p className="text-red-500 text-sm mb-4 animate-pulse">{passwordError}</p>}

                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={closeDeleteModal}
                                className="px-5 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium transition"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmDeleteWithPassword}
                                className="px-5 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium transition"
                            >
                                Delete Permanently
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <Footer />
        </div>
    );
};

export default DeleteProfileCan;
